import {
  createEmailReminder,
  listAllEmailReminders,
  updateEmailReminder,
  deleteEmailReminder,
} from '@/api/email-reminder/email-reminder';
import { ListAllEmailReminder } from '@/api/entities/email-reminder';
import { TEmailReminderSchema } from '@/schemas/email-reminder/email-reminder.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { ErrorResponse } from '@/utils/axios.http';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notify } from '@/utils/toast.utils';
import { NOTIFY } from '@/utils/constant.utils';

export const useCreateEmailReminder = ({
  t,
  showDefaultSuccessMessage,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TEmailReminderSchema) => createEmailReminder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listAllEmailReminders'],
      });

      if (showDefaultSuccessMessage) {
        notify(t('common:createSuccessful'), NOTIFY.SUCCESS);
      }

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error(error.error);
    },
  });
};

export const useListAllEmailReminders = (query?: ListAllEmailReminder) => {
  return useQuery({
    queryKey: ['listAllEmailReminders', query],
    queryFn: () => listAllEmailReminders(query),
  });
};

export const useUpdateEmailReminder = ({
  t,
  showDefaultSuccessMessage,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (emailReminder: TEmailReminderSchema) =>
      updateEmailReminder(emailReminder),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listAllEmailReminders'],
      });

      if (showDefaultSuccessMessage) {
        notify(t('common:updateSuccessful'), NOTIFY.SUCCESS);
      }

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when updating email reminder:', error);
    },
  });
};

export const useDeleteEmailReminder = ({
  t,
  showDefaultSuccessMessage,
  handleSuccess,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteEmailReminder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['listAllEmailReminders'],
      });

      if (showDefaultSuccessMessage) {
        notify(t('common:deleteSuccessful'), NOTIFY.SUCCESS);
      }

      handleSuccess?.();
    },
    onError: (error: ErrorResponse) => {
      notify(t('common:errorOccurredTryAgainLater'), NOTIFY.ERROR);
      console.error('Error when deleting email reminder:', error);
    },
  });
};
